import React, { useEffect, useRef, useState } from "react";
import { API } from "@/auth";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";

// Generate a stable session id per browser (persists across reloads for continuity)
function getSessionId() {
  const KEY = "lhs_chat_session";
  let id = localStorage.getItem(KEY);
  if (!id) {
    id = "chat_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(KEY, id);
  }
  return id;
}

const SUGGESTIONS = [
  "Quels moyens de paiement acceptez-vous ?",
  "Combien coûte la formule 3 mois ?",
  "Prenez-vous en charge Samsung Galaxy A12 ?",
  "Puis-je débloquer un iPhone avec Hello Bypass ?",
];

const WELCOME = "Bonjour 👋 Je suis l'assistant LHSunlocker. Comment puis-je vous aider ?";

export default function SupportChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ id: "welcome", role: "assistant", text: WELCOME }]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [err, setErr] = useState("");
  const scrollRef = useRef(null);
  const sessionId = useRef(getSessionId());
  const abortRef = useRef(null);
  const msgIdRef = useRef(1);
  const newId = () => `m_${msgIdRef.current++}_${Date.now()}`;

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || streaming) return;
    setInput("");
    setErr("");
    setMessages(m => [...m, { id: newId(), role: "user", text: q }, { id: newId(), role: "assistant", text: "", streaming: true }]);
    setStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const res = await fetch(`${API}/chat/support`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: q }),
        signal: controller.signal,
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({ detail: "Erreur" }));
        throw new Error(detail.detail || `HTTP ${res.status}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const chunks = buf.split("\n\n");
        buf = chunks.pop() ?? "";
        for (const chunk of chunks) {
          if (!chunk.startsWith("data: ")) continue;
          try {
            const evt = JSON.parse(chunk.slice(6));
            if (evt.delta) {
              setMessages(m => {
                const cp = [...m];
                cp[cp.length - 1] = { ...cp[cp.length - 1], text: cp[cp.length - 1].text + evt.delta };
                return cp;
              });
            } else if (evt.error) {
              setErr(evt.error);
            }
          } catch (parseErr) {
            console.warn("SSE parse error:", parseErr?.message);
          }
        }
      }
    } catch (e) {
      if (e.name !== "AbortError") setErr(e.message || "Erreur réseau");
    } finally {
      setStreaming(false);
      setMessages(m => {
        const cp = [...m];
        if (cp.length && cp[cp.length - 1].streaming) delete cp[cp.length - 1].streaming;
        return cp;
      });
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <>
      {/* Floating button */}
      <button
        data-testid="chat-fab"
        onClick={() => setOpen(o => !o)}
        aria-label="Ouvrir le chat support"
        className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 btn-orange rounded-full w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center shadow-2xl orange-glow transition-transform hover:scale-105"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6 sm:w-7 sm:h-7" />}
      </button>

      {/* Panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed inset-x-2 bottom-24 sm:inset-x-auto sm:right-6 sm:bottom-28 z-50 w-auto sm:w-[380px] max-w-[calc(100vw-16px)] h-[70vh] sm:h-[560px] card-lhs !rounded-2xl flex flex-col overflow-hidden orange-glow"
        >
          <div className="p-4 border-b border-zinc-800 bg-gradient-to-br from-orange-500/10 to-transparent flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-orange-500/15 border border-orange-500/40 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-orange-400" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-extrabold text-sm">Assistant LHSunlocker</div>
              <div className="text-[10px] text-emerald-400 font-mono-tech flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> En ligne · Claude Sonnet 4.6
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-zinc-400 hover:text-white" data-testid="chat-close">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto scroll-thin p-4 space-y-3" data-testid="chat-messages">
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-orange-500 text-black font-medium rounded-br-sm"
                    : "bg-zinc-900 text-zinc-100 border border-zinc-800 rounded-bl-sm"
                }`}>
                  {m.text || (m.streaming ? "…" : "")}
                  {m.streaming && m.text && <span className="inline-block w-1.5 h-3 bg-orange-400 ml-0.5 animate-pulse align-middle" />}
                </div>
              </div>
            ))}
            {err && (
              <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="chat-error">
                ⚠ {err}
              </div>
            )}
          </div>

          {messages.length <= 1 && (
            <div className="px-3 pb-2 flex flex-wrap gap-1.5" data-testid="chat-suggestions">
              {SUGGESTIONS.map(s => (
                <button key={s} onClick={() => send(s)}
                  className="text-[11px] px-2.5 py-1.5 rounded-full border border-zinc-700 text-zinc-300 hover:border-orange-500 hover:text-orange-400 transition">
                  {s}
                </button>
              ))}
            </div>
          )}

          <div className="p-3 border-t border-zinc-800 flex items-end gap-2 bg-zinc-950">
            <textarea
              data-testid="chat-input"
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder="Posez votre question…"
              disabled={streaming}
              className="input-lhs flex-1 !py-2 !text-sm resize-none max-h-24"
            />
            <button
              data-testid="chat-send"
              onClick={() => send()}
              disabled={streaming || !input.trim()}
              className="btn-orange rounded-lg w-10 h-10 flex items-center justify-center shrink-0 disabled:opacity-50"
              aria-label="Envoyer"
            >
              {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
          <div className="px-3 pb-2 text-[9px] text-zinc-500 font-mono-tech text-center">
            Assistant IA · Peut faire des erreurs · Contactez WhatsApp pour les cas critiques
          </div>
        </div>
      )}
    </>
  );
}
